import { type ChangeEvent, useState } from 'react'
import { Base } from '@itsrever/design-system'
import { Trans, useTranslation } from 'react-i18next'
import { ReturnProcess } from '@/domain/returns'
import { formatCurrency } from '@/utils'
import { Compensation } from '@/domain/refund'
import { MarkAsRefundedConfirmation } from '../mark-as-refunded-confirmation'

const {
  Input,
  Textarea,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  Button,
  DialogFooter,
  InfoTooltip
} = Base

interface MarkAsRefundedFormProps {
  onClose: () => void
  processDetails: ReturnProcess | undefined
}

export const MarkAsRefundedForm = ({
  onClose,
  processDetails
}: MarkAsRefundedFormProps) => {
  const compensation = processDetails?.compensation ?? ({} as Compensation)

  const pendingVal = parseFloat(
    compensation.statusSummary?.pendingRefundAmount?.amount ?? '0'
  )

  const currency =
    compensation.statusSummary?.pendingRefundAmount?.currency ?? 'EUR'

  const maxRefundVal = parseFloat(
    processDetails?.maximumRefundAmount?.amount ?? '0'
  )

  const [executedAmount, setExecutedAmount] = useState(pendingVal.toFixed(2))
  const [note, setNote] = useState('')
  const [errorNote, setErrorNote] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)

  const { t } = useTranslation()

  const executedVal = parseFloat(executedAmount)
  const difference = pendingVal - executedVal

  const isValidAmount =
    /^\d+(?:[.]\d+)?$/.test(executedAmount) && !isNaN(executedVal)

  const isOverMax = isValidAmount && executedVal > maxRefundVal

  const isDisabledSubmit = !isValidAmount || isOverMax

  return (
    <>
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault()

          if (note.length === 0) {
            setErrorNote(true)

            return
          }

          if (isValidAmount && !isOverMax) {
            setOpenConfirmation(true)
          }
        }}
      >
        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <label htmlFor="executed-amount" className="text-sm">
                <Trans
                  i18nKey="process_details_page.amount_executed_manually_label"
                  values={{ currency }}
                />
              </label>
              {/* Outside the label, so it stays out of the input's accessible name. */}
              <InfoTooltip
                label={t('process_details_page.about_amount_executed_manually')}
                content={t(
                  'process_details_page.amount_executed_manually_description'
                )}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Input
                id="executed-amount"
                autoComplete="off"
                value={executedAmount}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setExecutedAmount(e.target.value)
                }
                placeholder="0.00"
              />
              {!isValidAmount ? (
                <span className="pl-1 text-xs text-red-500">
                  {t('process_details_page.error_edited_amount')}
                </span>
              ) : isOverMax ? (
                <span className="pl-1 text-xs text-red-500">
                  <Trans
                    i18nKey="process_details_page.error_maximum_refund_amount"
                    values={{
                      maximumRefundAmount: formatCurrency(
                        maxRefundVal.toString(),
                        currency
                      )
                    }}
                  />
                </span>
              ) : null}
              {isValidAmount && !isOverMax && Math.abs(difference) > 0.001 && (
                <span className="pl-1 text-xs text-neutral-600">
                  {t('process_details_page.refund_difference', {
                    amount: formatCurrency(
                      Math.abs(difference).toFixed(2),
                      currency
                    ),
                    direction: t(
                      difference > 0
                        ? 'process_details_page.less'
                        : 'process_details_page.more'
                    )
                  })}
                </span>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="note" className="inline-block text-sm">
              {t('process_details_page.note_label')}
            </label>
            <div className="flex flex-col gap-2">
              <Textarea
                id="note"
                value={note}
                onChange={(e) => {
                  if (errorNote) {
                    setErrorNote(false)
                  }

                  setNote(e.target.value)
                }}
                className="resize-none font-sans"
                placeholder={t('process_details_page.note_placeholder')}
              />
              {errorNote ? (
                <span className="pl-1 text-xs leading-none text-red-500">
                  {t('process_details_page.note_error')}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('process_details_page.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isDisabledSubmit}>
            {t('process_details_page.mark_as_refunded')}
          </Button>
        </DialogFooter>
      </form>
      <DialogRoot open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogPortal>
          <DialogBackdrop forceRender className="opacity-40" />
          <DialogPopup>
            <MarkAsRefundedConfirmation
              onClose={() => setOpenConfirmation(false)}
              processId={processDetails?.processId ?? ''}
              executedAmount={executedAmount}
              currency={currency}
              note={note}
            />
          </DialogPopup>
        </DialogPortal>
      </DialogRoot>
    </>
  )
}
